import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react';
import type { LoginRequest, PublicUser } from '../types';
import { authService } from '../services/auth';
import { ApiError, UNAUTHORIZED_EVENT } from '../services/api';

interface AuthValue {
  user: PublicUser | null;
  loading: boolean;
  authModalOpen: boolean;
  openAuth: () => void;
  closeAuth: () => void;
  login: (input: Omit<LoginRequest, 'device_id'>) => Promise<void>;
  logout: () => Promise<void>;
}
const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    authService
      .current(controller.signal)
      .then(setUser)
      .catch((error) => {
        if (error instanceof ApiError && error.error === 'ArticleEvaluationUnauthorized') {
          setUser(null);
          return;
        }
        console.warn('standalone current user unavailable');
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, []);
  useEffect(() => {
    const open = () => {
      setUser(null);
      setAuthModalOpen(true);
    };
    addEventListener(UNAUTHORIZED_EVENT, open);
    return () => removeEventListener(UNAUTHORIZED_EVENT, open);
  }, []);
  const login = useCallback(async (input: Omit<LoginRequest, 'device_id'>) => {
    const data = await authService.login(input);
    setUser(data.user);
    setAuthModalOpen(false);
  }, []);
  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      setUser(null);
    }
  }, []);
  const value = useMemo(
    () => ({
      user,
      loading,
      authModalOpen,
      openAuth: () => setAuthModalOpen(true),
      closeAuth: () => setAuthModalOpen(false),
      login,
      logout
    }),
    [user, loading, authModalOpen, login, logout]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
